import { number, object, string } from 'yup';

import { regExpMac48 } from '@constants/regExp';
import {
	getMaxLengthErrMsg,
	getMinMaxDigitalValue,
	mandatoryErrMessage,
	validationErrMessages,
} from '@constants/validationConstants';
import { MAX_17_LENGTH } from '@constants/validationMessages';
import {
	ERUConfigurationFormFields,
	ERUConfigurationFormFieldsLabel,
	MAX_CONFIGURATION_HZ,
	MAX_DB,
	MAX_VLAN,
	MIN_CONFIGURATION_HZ,
	MIN_DB,
	MIN_VLAN,
} from './constants';

export const schema = object({
	[ERUConfigurationFormFields.name]: string(),
	[ERUConfigurationFormFields.vlanId]: number()
		.transform((value) => Number.isNaN(value) ? null : value)
		.typeError(mandatoryErrMessage(ERUConfigurationFormFieldsLabel.vlanId) || '')
		.min(
			MIN_VLAN,
			getMinMaxDigitalValue({
				name: ERUConfigurationFormFieldsLabel.vlanId,
				min: MIN_VLAN,
				max: MAX_VLAN,
			}),
		)
		.max(
			MAX_VLAN,
			getMinMaxDigitalValue({
				name: ERUConfigurationFormFieldsLabel.vlanId,
				min: MIN_VLAN,
				max: MAX_VLAN,
			}),
		)
		.required(mandatoryErrMessage(ERUConfigurationFormFields.vlanId))
		.test(ERUConfigurationFormFields.vlanId,
			getMinMaxDigitalValue({
				name: ERUConfigurationFormFieldsLabel.vlanId,
				min: MIN_VLAN,
				max: MAX_VLAN,
			}),
			(value) => {
				if (!value) {
					return false;
				}

				return Number.isInteger(value) && value >= MIN_VLAN && value <= MAX_VLAN;
			},
		)
		.nullable(),
	[ERUConfigurationFormFields.ruMacAddress]: string()
		.required(mandatoryErrMessage(ERUConfigurationFormFieldsLabel.ruMacAddress))
		.test(ERUConfigurationFormFields.ruMacAddress, mandatoryErrMessage(ERUConfigurationFormFieldsLabel.ruMacAddress) || '',
			(value) => {
				if (!value) {
					return false;
				}

				return regExpMac48.test(value);
			},
		),
	[ERUConfigurationFormFields.duMacAddress]: string()
		.required(mandatoryErrMessage(ERUConfigurationFormFieldsLabel.duMacAddress))
		.max(MAX_17_LENGTH, getMaxLengthErrMsg(ERUConfigurationFormFieldsLabel.duMacAddress, MAX_17_LENGTH))
		.test(ERUConfigurationFormFields.duMacAddress, mandatoryErrMessage(ERUConfigurationFormFieldsLabel.duMacAddress) || '',
			(value) => {
				if (!value) {
					return false;
				}

				return regExpMac48.test(value);
			},
		),
	[ERUConfigurationFormFields.mimoMode]: string(),
	[ERUConfigurationFormFields.frequency]: number()
		.transform((value) => Number.isNaN(value) ? null : value)
		.required(mandatoryErrMessage(ERUConfigurationFormFieldsLabel.frequency))
		.min(
			MIN_CONFIGURATION_HZ,
			getMinMaxDigitalValue({
				name: ERUConfigurationFormFieldsLabel.frequency,
				min: MIN_CONFIGURATION_HZ,
				max: MAX_CONFIGURATION_HZ,
			}),
		)
		.max(
			MAX_CONFIGURATION_HZ,
			getMinMaxDigitalValue({
				name: ERUConfigurationFormFieldsLabel.frequency,
				min: MIN_CONFIGURATION_HZ,
				max: MAX_CONFIGURATION_HZ,
			}),
		)
		.nullable(),
	[ERUConfigurationFormFields.nrafrcn]: number()
		.transform((value) => Number.isNaN(value) ? null : value)
		.required(mandatoryErrMessage(ERUConfigurationFormFieldsLabel.nrafrcn))
		.min(
			MIN_CONFIGURATION_HZ,
			getMinMaxDigitalValue({
				name: ERUConfigurationFormFieldsLabel.nrafrcn,
				min: MIN_CONFIGURATION_HZ,
				max: MAX_CONFIGURATION_HZ,
			}),
		)
		.max(
			MAX_CONFIGURATION_HZ,
			getMinMaxDigitalValue({
				name: ERUConfigurationFormFieldsLabel.nrafrcn,
				min: MIN_CONFIGURATION_HZ,
				max: MAX_CONFIGURATION_HZ,
			}),
		)
		.nullable(),
	[ERUConfigurationFormFields.channelBandwidth]: number()
		.transform((value) => Number.isNaN(value) ? null : value)
		.required(mandatoryErrMessage(ERUConfigurationFormFieldsLabel.channelBandwidth))
		.min(
			MIN_CONFIGURATION_HZ,
			getMinMaxDigitalValue({
				name: ERUConfigurationFormFieldsLabel.channelBandwidth,
				min: MIN_CONFIGURATION_HZ,
				max: MAX_CONFIGURATION_HZ,
			}),
		)
		.max(
			MAX_CONFIGURATION_HZ,
			getMinMaxDigitalValue({
				name: ERUConfigurationFormFieldsLabel.channelBandwidth,
				min: MIN_CONFIGURATION_HZ,
				max: MAX_CONFIGURATION_HZ,
			}),
		)
		.nullable(),
	[ERUConfigurationFormFields.transmissionGain]: number()
		.typeError(validationErrMessages.invalidValue)
		.min(
			MIN_DB,
			getMinMaxDigitalValue({
				name: ERUConfigurationFormFieldsLabel.transmissionGain,
				min: MIN_DB,
				max: MAX_DB,
			}),
		)
		.max(
			MAX_DB,
			getMinMaxDigitalValue({
				name: ERUConfigurationFormFieldsLabel.transmissionGain,
				min: MIN_DB,
				max: MAX_DB,
			}),
		)
		.required(validationErrMessages.transmissionGain)
		.test(
			ERUConfigurationFormFields.transmissionGain,
			validationErrMessages.transmissionGain,
			(value) => value === undefined || Number(value.toFixed(4)) === value,
		),
});
